import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  SpaceBetween,
  Container,
  Header,
  Icon,
  Alert,
  ProgressBar
} from '@cloudscape-design/components'
import { usePageHeader } from '../../../providers/PageHeaderProvider'
import { useAmplifyAuth } from '../../../providers/AmplifyAuthProvider'
import { useQuery, useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import AssetHierarchy from '../components/AssetHierarchy'
import AssetDetails from '../components/AssetDetails'
import type { Id } from '../../../../convex/_generated/dataModel'

interface SelectedAsset {
  _id: string
  name: string
  type: 'factory' | 'production_line' | 'equipment_component'
  status: string
  external_asset_id: string
  sitewise_asset_id?: string
}

export default function AssetsPage() {
  const { sessionId } = useAmplifyAuth()
  const { setHeaderConfig } = usePageHeader()
  const [selectedAsset, setSelectedAsset] = useState<SelectedAsset | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isSyncingExternalIds, setIsSyncingExternalIds] = useState(false)
  
  // Action for syncing external IDs
  const syncExternalIds = useAction(api.components.assets.index.syncExternalIds)

  // Real Convex queries for asset statistics
  const factories = useQuery(
    sessionId ? api.components.assets.index.getFactories : "skip",
    sessionId ? { sessionId } : "skip"
  )
  const productionLines = useQuery(
    sessionId ? api.components.assets.index.getProductionLines : "skip",
    sessionId ? { sessionId } : "skip"
  )
  const equipment = useQuery(
    sessionId ? api.components.assets.index.getEquipmentComponents : "skip",
    sessionId ? { sessionId } : "skip"
  )

  // Calculate asset statistics
  const assetStats = useMemo(() => {
    return {
      totalFactories: factories?.length || 0,
      totalProductionLines: productionLines?.length || 0,
      totalEquipment: equipment?.length || 0,
      operationalLines: productionLines?.filter(line => line.status === 'running').length || 0
    }
  }, [factories, productionLines, equipment])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    // The queries will automatically refresh since they're reactive
    setTimeout(() => {
      setIsRefreshing(false)
    }, 1500)
  }

  const handleSyncExternalIds = async () => {
    if (!sessionId) return
    
    setIsSyncingExternalIds(true)
    try {
      const result = await syncExternalIds({ sessionId })
      console.log('✅ External ID sync completed:', result)
      // Show success message - you could add a toast here
    } catch (error) {
      console.error('❌ External ID sync failed:', error)
      // Show error message - you could add a toast here
    } finally {
      setIsSyncingExternalIds(false)
    }
  }

  // Memoize header actions to prevent unnecessary re-renders
  const headerActions = useMemo(() => {
    const actions = []
    
    actions.push(
      {
        id: 'sync-external-ids',
        component: (
          <Button 
            onClick={handleSyncExternalIds}
            iconName="sync"
            disabled={isSyncingExternalIds}
            loading={isSyncingExternalIds}
          >
            {isSyncingExternalIds ? 'Syncing...' : 'Sync External IDs'}
          </Button>
        )
      },
      {
        id: 'refresh-data',
        component: (
          <Button 
            onClick={handleRefresh}
            iconName="refresh"
            disabled={isRefreshing}
            loading={isRefreshing}
          >
            Refresh Data
          </Button>
        )
      },
      {
        id: 'discover-assets',
        component: (
          <Button 
            variant="primary" 
            href="/assets/discover"
            iconName="external"
          >
            Discover Assets
          </Button>
        )
      }
    )
    
    return actions
  }, [isRefreshing, isSyncingExternalIds])

  // Set page header
  useEffect(() => {
    setHeaderConfig({
      title: 'Asset Explorer',
      description: `${assetStats.totalFactories} factories, ${assetStats.totalProductionLines} production lines, ${assetStats.totalEquipment} equipment components`,
      actions: headerActions
    })
  }, [headerActions, assetStats])



  return (
    <SpaceBetween size="m">
      {/* Refresh Status */}
      {isRefreshing && (
        <Alert
          statusIconAriaLabel="Info"
          header="Refreshing Asset Data"
        >
          <SpaceBetween size="s">
            <div>Fetching latest asset information...</div>
            <ProgressBar variant="indeterminate" />
          </SpaceBetween>
        </Alert>
      )}

      <div style={{ 
        display: 'flex', 
        gap: '12px',
        height: 'calc(100vh - 200px)'
      }}>

        {/* Left Side - Asset Detail */}
        <div style={{ 
          flex: 1,
          overflow: 'auto',
          maxHeight: 'calc(100vh - 200px)'
        }}>
          {selectedAsset ? (
            <AssetDetails 
              asset={selectedAsset}
              onClose={() => setSelectedAsset(null)}
            />
          ) : (
            <Container header={<Header variant="h3">Asset Information</Header>}>
              <Box textAlign="center" color="text-body-secondary" padding="xxl">
                <SpaceBetween size="m">
                  <Icon name="status-info" size="big" />
                  <div>Select an asset from the hierarchy to view details</div>
                  {assetStats.totalFactories > 0 && (
                    <div style={{ fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
                      Click on any Factory, Production Line, or Equipment Component to view:
                      <ul style={{ textAlign: 'left', marginTop: '8px' }}>
                        <li>Real-time property values</li>
                        <li>Asset configuration details</li>
                        <li>External SiteWise asset mapping</li>
                        <li>Property mappings and data types</li>
                      </ul>
                    </div>
                  )}
                </SpaceBetween>
              </Box>
            </Container>
          )}
        </div>
          
        {/* Right Side - Asset Hierarchy */}
        <AssetHierarchy 
          onAssetSelect={setSelectedAsset}
          selectedAsset={selectedAsset}
        />
      </div>
    </SpaceBetween>
  )
}